import { useEffect, useRef } from 'react';

/**
 * Runs `fn` immediately, then on a `delayMs` interval. Skips the tick when
 * the tab is hidden, and re-fires immediately on visibilitychange so the
 * user sees fresh data the moment they refocus the tab.
 *
 * Replaces inline `setInterval(fn, 30_000)` calls that wasted bandwidth +
 * battery in background tabs across the dashboard / tournament poll loops.
 *
 * The latest `fn` is kept in a ref so callers don't have to memoize the
 * callback — the interval always invokes the most recent closure.
 */
export function usePollingInterval(fn, delayMs) {
	const savedFn = useRef(fn);
	useEffect(() => {
		savedFn.current = fn;
	}, [fn]);

	useEffect(() => {
		if (typeof window === 'undefined' || !delayMs) return undefined;

		const tick = () => {
			if (typeof document !== 'undefined' && document.hidden) return;
			savedFn.current?.();
		};

		// Fire once on mount so the page renders with data immediately.
		tick();

		const intervalId = setInterval(tick, delayMs);

		const onVisibilityChange = () => {
			if (!document.hidden) tick();
		};
		document.addEventListener('visibilitychange', onVisibilityChange);

		return () => {
			clearInterval(intervalId);
			document.removeEventListener('visibilitychange', onVisibilityChange);
		};
	}, [delayMs]);
}
